import { all, delay, put, select, takeEvery } from 'redux-saga/effects';
import type { ActionType } from 'typesafe-actions';

import { IErknmAnalyticsData } from 'APIPackage/ErknmAnalyticsModule/interfaces/IErknmAnalyticsData';
import {
  fetchErknmAnalytics,
  fetchErknmAnalyticsExport,
  fetchErknmAnalyticsExportKnmList,
  fetchErknmAnalyticsFilter,
  fetchErknmAnalyticsKnm,
} from 'APIPackage/ErknmAnalyticsModule/services/erknmAnalyticsApi';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IErknmAnalyticsFilterRaw } from 'APIPackage/ErknmAnalyticsModule/interfaces/IErknmAnalyticsFilterRaw';
import { notifyExportError, notifyExportSuccess } from 'CorePackage/MainModule/sagas/commonNotificationSagas';
import { DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import {
  dictionariesKnmKindsItemGet,
  dictionariesKnoOrganizationsItemGet,
} from 'CorePackage/DictionariesModule/actions/dictionariesActions';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IKnmKind } from 'APIPackage/DictionariesModule/interfaces/IKnmKind';
import { catalogGet } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import {
  getCatalogsProsecutorOrganizationsSelectOptions,
  getCatalogsAnalyticsTerritorialUnitsSelectOptions,
} from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IExport } from 'APIPackage/ImportExportModule/interfaces/IExport';
import { formatDate, formatDateLocale } from 'CorePackage/MainModule/common/formatDate';
import { IKnmIndexPage } from 'APIPackage/KnmModule/interfaces/IKnmIndexPage';
import { mapRawToData } from 'KnmListPackage/MainModule/common/mapRawToData';
import { getCoreIsLoading } from 'CorePackage/MainModule/selectors/coreSelectors';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';
import { IProsecutorOrganizationsSelectOption } from 'CorePackage/CatalogsModule/interfaces/IProsecutorOrganizationsSelectOption';
import { IAnalyticsTerritorialUnitSelectOption } from 'CorePackage/CatalogsModule/interfaces/IAnalyticsTerritorialUnitSelectOption';
import { ITerritorialUnitSelectOption } from 'CorePackage/CatalogsModule/interfaces/ITerritorialUnitSelectOption';

import {
  erknmAnalyticsExport,
  erknmAnalyticsExportKnmList,
  erknmAnalyticsGetKnmList,
  erknmAnalyticsGetKnmListNextPage,
  erknmAnalyticsSetData,
  erknmAnalyticsSetFilter,
  erknmAnalyticsSetKnmList,
  erknmAnalyticsSetTableColumns,
} from '../actions/erknmAnalyticsActions';
import { ErknmAnalyticsActionsEnum } from '../enums/ErknmAnalyticsActionsEnum';
import {
  getErknmAnalyticsFilter,
  getErknmAnalyticsKnmKindSelectOptions,
  getErknmAnalyticsKnmOrder,
  getErknmAnalyticsTableColumns,
} from '../selectors/erknmAnalyticsSelectors';
import { IErknmAnalyticsFilter } from '../interfaces/IErknmAnalyticsFilter';
import { convertColumnsDataToTableColumns } from '../common/convertColumnsDataToTableColumns';
import { initialErknmAnalyticsTableColumns } from '../contstants/initialErknmAnalyticsTableColumns';
import { IKnoOrganizationDictionary } from '../../../APIPackage/DictionariesModule/interfaces/IKnoOrganizationDictionary';
import { getDictionariesKnoOrganizationsSelectOptions } from '../../../CorePackage/DictionariesModule/selectors/dictionariesSelectors';

export function* watchErknmAnalyticsSaga() {
  yield takeEvery(ErknmAnalyticsActionsEnum.ERKNM_ANALYTICS_INIT_PAGE, erknmAnalyticsInitPageSaga);
  yield takeEvery(ErknmAnalyticsActionsEnum.ERKNM_ANALYTICS_GET_DATA, erknmAnalyticsGetDataSaga);
  yield takeEvery(ErknmAnalyticsActionsEnum.ERKNM_ANALYTICS_GET_DATA_NEXT, erknmAnalyticsGetDataNextSaga);
  yield takeEvery(ErknmAnalyticsActionsEnum.ERKNM_ANALYTICS_EXPORT, erknmAnalyticsExportSaga);
  yield takeEvery(ErknmAnalyticsActionsEnum.ERKNM_ANALYTICS_EXPORT_KNM_LIST, erknmAnalyticsExportKnmListSaga);
  yield takeEvery(ErknmAnalyticsActionsEnum.ERKNM_ANALYTICS_GET_KNM_LIST, erknmAnalyticsGetKnmListSaga);
  yield takeEvery(
    ErknmAnalyticsActionsEnum.ERKNM_ANALYTICS_GET_KNM_LIST_NEXT_PAGE,
    erknmAnalyticsGetKnmListNextPageSaga,
  );
}

const erknmAnalyticsPageCatalogs: CommonCatalogsNamesEnum[] = [
  CommonCatalogsNamesEnum.TERRITORIAL_UNITS_BY_LEVELS_COUNTRY_FEDERAL_DISTRICTS_REGIONS,
  CommonCatalogsNamesEnum.TERRITORIAL_UNITS,
  CommonCatalogsNamesEnum.PROSECUTOR_ORGANIZATIONS,
  CommonCatalogsNamesEnum.ANALYTICS_TERRITORIAL_UNITS,
  CommonCatalogsNamesEnum.USER_KNOS,
];

function* erknmAnalyticsInitPageSaga() {
  yield put(dictionariesKnmKindsItemGet());
  yield put(dictionariesKnoOrganizationsItemGet());
  yield all(erknmAnalyticsPageCatalogs.map(catalogName => put(catalogGet(catalogName))));
  yield delay(1000);
}

function* erknmAnalyticsGetDataSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const knmKindSelectOptions: Array<IDictionarySelectOption<IKnmKind>> =
      getErknmAnalyticsKnmKindSelectOptions(state) ?? [];
    const territorialUnitsSelectOptions: Array<IAnalyticsTerritorialUnitSelectOption> = getCatalogsAnalyticsTerritorialUnitsSelectOptions(
      state,
    );
    const prosecutorOrganizationsSelectOptions: Array<IProsecutorOrganizationsSelectOption> = getCatalogsProsecutorOrganizationsSelectOptions(
      state,
    );
    const knoSelectOptions: IDictionarySelectOption<IKnoOrganizationDictionary>[] =
      getDictionariesKnoOrganizationsSelectOptions(state) ?? [];
    let filter: IErknmAnalyticsFilter | undefined = getErknmAnalyticsFilter(state);

    if (!filter) {
      const filterData: IErknmAnalyticsFilterRaw = yield fetchErknmAnalyticsFilter();

      filter = convertFilterRawToForm(
        filterData,
        knmKindSelectOptions,
        territorialUnitsSelectOptions,
        knoSelectOptions,
        prosecutorOrganizationsSelectOptions,
      );
      yield put(erknmAnalyticsSetFilter(filter));
    }
    const rawFilter: IErknmAnalyticsFilterRaw | undefined = filter ? convertFilterFormToRaw(filter) : undefined;
    const res: IErknmAnalyticsData = yield fetchErknmAnalytics(0, rawFilter);

    yield put(erknmAnalyticsSetData(res.rows, res.page, true, res.localDateTime));
    const columns = getErknmAnalyticsTableColumns(state);

    if (columns === initialErknmAnalyticsTableColumns) {
      yield put(
        erknmAnalyticsSetTableColumns(
          convertColumnsDataToTableColumns(res.columns) ?? initialErknmAnalyticsTableColumns,
        ),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* erknmAnalyticsGetDataNextSaga() {
  const state: IRootState = yield select();

  function* funcTry() {
    const filter: IErknmAnalyticsFilter | undefined = getErknmAnalyticsFilter(state);

    if (filter && state.erknmAnalytics.hasMoreData) {
      const rawFilter: IErknmAnalyticsFilterRaw | undefined = filter ? convertFilterFormToRaw(filter) : undefined;

      const res: IErknmAnalyticsData = yield fetchErknmAnalytics(state.erknmAnalytics.page, rawFilter);

      yield put(
        erknmAnalyticsSetData(
          [...state.erknmAnalytics.data, ...res.rows],
          res.page,
          res.rows.length > 0,
          res.localDateTime,
        ),
      );
    }
  }

  const isLoading = getCoreIsLoading(state);

  if (!isLoading) {
    yield sagaWrapper({
      funcTry,
    });
  }
}

function* erknmAnalyticsExportSaga(action: ActionType<typeof erknmAnalyticsExport>) {
  function* funcTry() {
    const { format } = action.payload;
    const filter: IErknmAnalyticsFilter | undefined = yield select(getErknmAnalyticsFilter);

    const rawFilter: IErknmAnalyticsFilterRaw | undefined = filter ? convertFilterFormToRaw(filter) : undefined;
    const meta: IExport = yield fetchErknmAnalyticsExport(format, rawFilter);

    yield notifyExportSuccess(meta.id);
  }
  function* funcCatch() {
    yield notifyExportError();
  }
  yield sagaWrapper({
    funcTry,
    funcCatch,
  });
}

const convertFilterRawToForm = (
  filterData: IErknmAnalyticsFilterRaw,
  knmKindOptions: Array<IDictionarySelectOption<IKnmKind>>,
  analyticsTerritorialUnits: Array<ITerritorialUnitSelectOption>,
  knoSelectOptions: IDictionarySelectOption<IKnoOrganizationDictionary>[],
  prosecutorOrganizationsSelectOptions: Array<IProsecutorOrganizationsSelectOption>,
): IErknmAnalyticsFilter => ({
  ...filterData,
  districts: analyticsTerritorialUnits.filter(item => filterData.districts.find(id => id === item.id) !== undefined),
  prosecutorOrganizations: prosecutorOrganizationsSelectOptions.filter(
    item => filterData.ervkProsecutorOrganizationIds?.find(id => id === item.id) !== undefined,
  ),
  knmKind: knmKindOptions.find(item => item.code === filterData.knmKind) ?? null,
  kno: knoSelectOptions.filter(
    item => filterData.knoIds.find(dictVersionId => dictVersionId === item.value.dictVersionId) !== undefined,
  ),
  startDate: filterData.startDate
    ? formatDateLocale({
        date: filterData.startDate,
        dateFormat: DATE_FORMAT_LONG_FNS,
      })
    : undefined,
  stopDate: filterData.stopDate
    ? formatDateLocale({
        date: filterData.stopDate,
        dateFormat: DATE_FORMAT_LONG_FNS,
      })
    : undefined,
});

function convertFilterFormToRaw(filter: IErknmAnalyticsFilter): IErknmAnalyticsFilterRaw {
  const {
    id,
    name,
    districts,
    prosecutorOrganizations,
    knmKind,
    kno,
    startDate,
    stopDate,
    withInnerDistricts: isInnerDistricts,
    withTerritoryAgencies: isTerritoryAgencies,
    withSubProsecutorOrganizations,
  } = filter;
  const urlParams = new URLSearchParams(window.location.search);
  const withTrace = urlParams.get('withTrace');
  const version = Number(urlParams.get('version'));

  return {
    id: id ?? null,
    name: name ?? null,
    knoIds: kno?.map(item => item.id) ?? [],
    districts: districts?.map<number>(item => item.id) ?? [],
    ervkProsecutorOrganizationIds: prosecutorOrganizations?.map(item => item.id) ?? [],
    knmKind: knmKind?.code ?? null,
    startDate: startDate
      ? formatDate({
          date: startDate,
          dateFormat: SYSTEM_DATE_FORMAT_FNS,
        })
      : null,
    stopDate: stopDate
      ? formatDate({
          date: stopDate,
          dateFormat: SYSTEM_DATE_FORMAT_FNS,
        })
      : null,
    withInnerDistricts: isInnerDistricts,
    withTerritoryAgencies: isTerritoryAgencies,
    withSubProsecutorOrganizations,
    ...(withTrace && { withTrace: withTrace === 'true' }),
    ...(version && { version }),
  };
}

function* erknmAnalyticsExportKnmListSaga(action: ActionType<typeof erknmAnalyticsExportKnmList>) {
  function* funcTry() {
    const { r, c, f, filter, format } = action.payload;
    const rawFilter: IErknmAnalyticsFilterRaw | undefined = filter ? convertFilterFormToRaw(filter) : undefined;
    const meta: IExport = yield fetchErknmAnalyticsExportKnmList(r, c, f, format, rawFilter);

    yield notifyExportSuccess(meta.id);
  }
  function* funcCatch() {
    yield notifyExportError();
  }
  yield sagaWrapper({
    funcTry,
    funcCatch,
  });
}

function* erknmAnalyticsGetKnmListSaga(action: ActionType<typeof erknmAnalyticsGetKnmList>) {
  function* funcTry() {
    const { r, c, f, filter } = action.payload;
    const knmOrder: string | null = yield select(getErknmAnalyticsKnmOrder);
    const rawFilter: IErknmAnalyticsFilterRaw | undefined = filter ? convertFilterFormToRaw(filter) : undefined;
    const res: IKnmIndexPage = yield fetchErknmAnalyticsKnm(r, c, f, 0, 20, rawFilter, knmOrder);

    yield put(erknmAnalyticsSetKnmList(mapRawToData(res.list, true), res.page, res.pageCount, res.totalCount));
  }
  yield sagaWrapper({
    funcTry,
  });
}

function* erknmAnalyticsGetKnmListNextPageSaga(action: ActionType<typeof erknmAnalyticsGetKnmListNextPage>) {
  function* funcTry() {
    const { r, c, f, filter } = action.payload;
    const state: IRootState = yield select();
    const rawFilter: IErknmAnalyticsFilterRaw | undefined = filter ? convertFilterFormToRaw(filter) : undefined;
    const res: IKnmIndexPage = yield fetchErknmAnalyticsKnm(
      r,
      c,
      f,
      state.erknmAnalytics.knmPage + 1,
      20,
      rawFilter,
      state.erknmAnalytics.knmOrder,
    );

    yield put(
      erknmAnalyticsSetKnmList(
        [...state.erknmAnalytics.knmList, ...mapRawToData(res.list, true)],
        res.page,
        res.pageCount,
        res.totalCount,
      ),
    );
  }
  yield sagaWrapper({
    funcTry,
  });
}
